import { useEffect, useRef, useState } from "react";

interface Option {
  value: string;
  label: string;
}

interface SelectWithAddNewProps {
  options: Option[];
  value: string;
  onChange: (value: string) => void;
  onAdd: (label: string) => void;
  validateNew?: (label: string) => string | null;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export function SelectWithAddNew({
  options,
  value,
  onChange,
  onAdd,
  validateNew,
  placeholder = "Select an option",
  className = "",
  disabled = false,
}: SelectWithAddNewProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current) searchInputRef.current.focus();
  }, [isOpen]);

  const selected = options.find((o) => o.value === value);

  const trimmed = searchTerm.trim();
  const lower = trimmed.toLowerCase();
  const filteredOptions = options.filter((o) => o.label.toLowerCase().includes(lower));
  const exactMatch = options.some((o) => o.label.toLowerCase() === lower);
  const canShowAdd = trimmed.length > 0 && !exactMatch;
  const addValidationError = canShowAdd && validateNew ? validateNew(trimmed) : null;

  function handleSelect(val: string) {
    onChange(val);
    setIsOpen(false);
    setSearchTerm("");
  }

  function handleAdd() {
    if (!canShowAdd || addValidationError) return;
    onAdd(trimmed);
    setIsOpen(false);
    setSearchTerm("");
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      e.preventDefault();
      if (canShowAdd && !addValidationError) {
        handleAdd();
      } else if (filteredOptions.length === 1) {
        handleSelect(filteredOptions[0].value);
      }
    }
  }

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full border p-3 text-left bg-white dark:bg-primary-800 flex justify-between items-center ${
          disabled
            ? "cursor-not-allowed opacity-50 bg-gray-50 dark:bg-primary-900 border-gray-200 dark:border-primary-700"
            : "text-primary-500 dark:text-white border-gray-200 dark:border-primary-700 hover:border-primary-400 dark:hover:border-primary-500 focus:outline-none focus:border-primary-600"
        }`}
      >
        <span className={!selected ? "text-gray-400 dark:text-gray-500" : ""}>
          {selected ? selected.label : placeholder}
        </span>
        <svg
          className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white dark:bg-primary-800 border border-gray-200 dark:border-primary-700 rounded-md shadow-lg">
          <div className="p-2 border-b border-gray-200 dark:border-primary-700 sticky top-0 bg-white dark:bg-primary-800">
            <input
              ref={searchInputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search or type to add..."
              className="w-full px-3 py-2 border border-gray-300 dark:border-primary-600 rounded-md focus:outline-none focus:border-primary-500 text-sm bg-white dark:bg-primary-900 dark:text-white"
              onClick={(e) => e.stopPropagation()}
            />
            {canShowAdd && addValidationError && (
              <p className="text-xs text-red-600 mt-2">{addValidationError}</p>
            )}
          </div>

          <div className="max-h-60 overflow-auto">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <div
                  key={option.value}
                  onClick={() => handleSelect(option.value)}
                  className={`px-3 py-3 cursor-pointer hover:bg-primary-50 dark:hover:bg-primary-700 border-b border-gray-100 dark:border-primary-700 last:border-b-0 ${
                    value === option.value ? "bg-primary-50 dark:bg-primary-700" : ""
                  }`}
                >
                  <div className="font-medium text-primary-600 dark:text-white">{option.label}</div>
                </div>
              ))
            ) : (
              !canShowAdd && (
                <div className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400 text-center">No results found</div>
              )
            )}

            {canShowAdd && (
              <button
                type="button"
                onClick={handleAdd}
                disabled={!!addValidationError}
                className="w-full px-3 py-3 text-left border-t border-gray-200 dark:border-primary-700 hover:bg-primary-50 dark:hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <svg className="w-4 h-4 text-primary-600 dark:text-primary-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span className="text-sm text-primary-600 dark:text-primary-300">
                  Add &quot;<strong>{trimmed}</strong>&quot;
                </span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default SelectWithAddNew;
